/**
 * TopNavLink — active-underline primitive for the desktop top nav.
 *
 * Shows label, badge (optional). Active state uses a coral underline
 * that sits on top of the surface border. Focus-visible coral ring
 * comes from `global.css` so we don't re-declare it here.
 */
import { useReducedMotion } from 'react-native-reanimated'
import React, { useState } from 'react'
import { View, Text, Platform } from 'react-native'
import { useRouter } from 'expo-router'
import { Pressable } from '../ui/Pressable'
import { useTheme } from '../../lib/hooks/use-theme'
import { useSemanticColors, DURATION } from '../../lib/theme/tokens'
import { COLORS, FONTS, SPACING } from '../../lib/brand'
import { CountBadge } from '../ui/CountBadge'

interface TopNavLinkProps {
  label: string
  href: string
  active?: boolean
  badge?: number
  testID?: string
}

const isWeb = Platform.OS === 'web'

export function TopNavLink({ label, href, active, badge, testID }: TopNavLinkProps) {
  const reducedMotion = useReducedMotion()
  const router = useRouter()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const [hover, setHover] = useState(false)

  const color = active ? colors.textPrimary : hover ? colors.textPrimary : colors.textSecondary

  return (
    <Pressable
      onPress={() => router.navigate(href as never)}
      onHoverIn={() => setHover(true)}
      onHoverOut={() => setHover(false)}
      accessibilityRole="link"
      accessibilityLabel={label}
      testID={testID}
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: SPACING.xs,
        paddingVertical: SPACING.sm + 4,
        paddingHorizontal: SPACING.md,
        position: 'relative',
        ...(isWeb
          ? {
              transition: reducedMotion
                ? undefined
                : (`color ${DURATION.fast}ms ease-out` as unknown as undefined),
            }
          : null),
      }}
    >
      <Text
        style={{
          fontSize: 14,
          fontFamily: active ? FONTS.bodyBold : FONTS.body,
          color,
          ...(isWeb
            ? {
                transition: reducedMotion
                  ? undefined
                  : (`color ${DURATION.fast}ms ease-out` as unknown as undefined),
              }
            : null),
        }}
      >
        {label}
      </Text>
      <CountBadge count={badge ?? 0} />
      {/* Underline: always rendered, scales in on active so the transition is
          springy rather than a hard swap. Coral → scale(1) on active, 0 otherwise. */}
      <View
        style={{
          position: 'absolute',
          left: SPACING.md,
          right: SPACING.md,
          bottom: 0,
          height: 2,
          backgroundColor: COLORS.coral.DEFAULT,
          borderRadius: 1,
          opacity: active ? 1 : 0,
          transform: [{ scaleX: active ? 1 : 0.2 }],
          ...(isWeb
            ? ({
                transition: reducedMotion
                  ? undefined
                  : `opacity ${DURATION.fast}ms cubic-bezier(0.16, 1, 0.3, 1), transform ${DURATION.normal}ms cubic-bezier(0.16, 1, 0.3, 1)`,
              } as unknown as object)
            : null),
        }}
      />
    </Pressable>
  )
}
